import mitt from "mitt";
import * as PIXI from "pixi.js";
import { KawaseBlurFilter } from "@pixi/filter-kawase-blur";
import debounce from "lodash/debounce";
import { createNoise2D } from "simplex-noise";
import { fsAnimationFrame } from "./fps-animate";
import { gsap } from "./gsap";

// const CreateOrbEffectParam = {
//   container: HTMLCanvasElement;
//   colorChoices: string[];
//   originPointer: {
//       x: number;
//       y: number;
//   };
//   blurOption?: {
//       blur: number;
//       quality: number;
//   };
//   getSize(): { width: number; height: number };
//   setBounds?: () => ReturnType<Orb["setBounds"]>;
//   onResize?: () => void;
//   orbParam: Partial<OrbParam>;
// }

// return a random number within a range
function random(min, max) {
  return Math.random() * (max - min) + min;
}

function map(n, start1, end1, start2, end2) {
  return ((n - start1) / (end1 - start1)) * (end2 - start2) + start2;
}

const noise2D = createNoise2D();

class ColorPalette {
  constructor(colorChoices = ["#3af6de", "#00c0ff"]) {
    this.colorChoices = colorChoices;
    this.setCustomProperties();
  }

  setColors(colorChoices) {
    // store the color choices in an array so that a random one can be picked later
    this.colorChoices = colorChoices;
  }

  randomColor(i) {
    // pick a random color
    return this.colorChoices[i].replace("#", "0x");
  }

  setCustomProperties() {
    // set CSS custom properties so that the colors defined here can be used throughout the UI
  }
}

class Orb {
  x;
  y;

  // how large the orb is vs it's original radius (this will modulate over time)
  scale = 1;
  fill;
  radius;

  // starting points in "time" for the noise/self similar random values
  xOff = random(0, 1000);
  yOff = random(0, 1000);
  inc;
  bounds;
  graphics;

  mittManage = mitt();

  // Pixi takes hex colors as hexidecimal literals (0x rather than a string with '#')
  constructor(fill = 0x000000, param) {
    this.param = Object.assign(
      {
        radius: Math.min(window.innerHeight * 0.3, window.innerWidth * 0.3),
        scaleRange: [0.5, 1]
      },
      param
    );

    const setBoundsHandle = this.param.setBounds
      ? this.param.setBounds
      : this.setBounds;

    this.bounds = setBoundsHandle();

    const { x, y } = this.param.getOrigin() || {
      x: this.bounds.originX,
      y: this.bounds.originY
    };
    // initialise the orb's { x, y } values to a random point within it's bounds
    this.x = random(x + this.bounds["x"].min, x + this.bounds["x"].max);
    this.y = random(y + this.bounds["y"].min, y + this.bounds["y"].max);

    // what color is the orb?
    this.fill = fill;

    // the original radius of the orb, set relative to window height
    this.radius = this.param.radius || 0;

    // how quickly the noise/self similar random values step through time
    this.inc = 0.002;

    // PIXI.Graphics is used to draw 2d primitives (in this case a circle) to the canvas
    this.graphics = new PIXI.Graphics();
    // this.graphics.alpha = 0.825;

    // 250ms after the last window resize event, recalculate orb positions.
    const debounceResize = debounce(() => {
      this.bounds = setBoundsHandle();
    }, 250);
    window.addEventListener("resize", debounceResize);
    this.mittManage.on("destroy", () => {
      window.removeEventListener("resize", debounceResize);
      this.graphics.destroy();
    });
  }

  setBounds() {
    const maxDistX = window.innerWidth / 5;
    const maxDistY = window.innerWidth / 5;

    const originX = window.innerWidth / 1.25;
    const originY =
      window.innerWidth < 1000
        ? window.innerHeight
        : window.innerHeight / 1.375;

    return {
      originX,
      originY,
      x: { min: -maxDistX, max: +maxDistX },
      y: { min: -maxDistY, max: +maxDistY }
    };
  }

  update() {
    const { x, y } = this.param.getOrigin() || {
      x: this.bounds.originX,
      y: this.bounds.originY
    };

    // self similar "psuedo-random" or noise values at a given point in "time"
    const xNoise = noise2D(this.xOff, this.xOff);
    const yNoise = noise2D(this.yOff, this.yOff);
    const scaleNoise = noise2D(this.xOff, this.yOff);

    // map the xNoise/yNoise values (between -1 and 1) to a point within the orb's bounds
    this.x = map(
      xNoise,
      -1,
      1,
      x + this.bounds["x"].min,
      x + this.bounds["x"].max
    );
    this.y = map(
      yNoise,
      -1,
      1,
      y + this.bounds["y"].min,
      y + this.bounds["y"].max
    );

    // map scaleNoise (between -1 and 1) to a scale value somewhere between half of the orb's original size, and 100% of it's original size
    this.scale = map(scaleNoise, -1, 1, 0.5, 1);

    // step through "time"
    this.xOff += this.inc;
    this.yOff += this.inc;
  }

  render() {
    // update the PIXI.Graphics position and scale values
    this.graphics.x = this.x;
    this.graphics.y = this.y;
    this.graphics.scale.set(this.scale);

    // clear anything currently drawn to graphics
    this.graphics.clear();

    // tell graphics to fill any shapes drawn after this with the orb's fill color
    this.graphics.beginFill(this.fill);
    // draw a circle at { 0, 0 } with it's size set by this.radius
    this.graphics.drawCircle(0, 0, this.radius);
    // let graphics know we won't be filling in any more shapes
    this.graphics.endFill();
  }

  destroy() {
    this.mittManage.emit("destroy");
    this.mittManage.all.clear();
  }
}

export class createOrbEffect {
  mittManage = mitt();

  pixiApp = null;
  circleState = {
    catch: false,
    position: {
      x: window.innerWidth / 2,
      y: window.innerHeight / 2
    }
  };
  state = "";

  orbs = [];
  renderFrame = fsAnimationFrame(60);
  originPointer = {};
  colorPalette = null;

  constructor(param) {
    this.param = Object.assign(
      {
        colorChoices: ["#3af6de", "#00c0ff"],
        originPointer: {
          x: window.innerWidth / 2,
          y: window.innerHeight / 2
        },
        orbPram: {}
      },
      param
    );
    this.originPointer = this.param.originPointer || { x: 0, y: 0 };
  }

  init() {
    this.pixiApp = new PIXI.Application({
      view: this.param.container,
      backgroundAlpha: 0
    });

    this.createColorPalette();
    this.initOrb();
    this.resize();

    const debounceResize = debounce(this.resize, 250).bind(this);
    $(window).on("resize.orbEffect", debounceResize);

    this.mittManage.on("destroy", () => {
      $(window).off("resize.orbEffect", debounceResize);
      this.pixiApp && this.pixiApp.destroy();
    });

    return this;
  }
  createColorPalette() {
    this.colorPalette = new ColorPalette(this.param.colorChoices);
    const { blur, quality } = this.param.blurOption || {
      blur: 55,
      quality: 15
    };
    if (!this.pixiApp) return;
    this.pixiApp.stage.filters = [new KawaseBlurFilter(blur, quality, true)];
  }

  initOrb() {
    if (!this.colorPalette) return;
    for (let i = 0; i < this.colorPalette.colorChoices.length; i++) {
      const orb = new Orb(this.colorPalette.randomColor(i), {
        getOrigin: () => {
          return this.originPointer;
        },
        setBounds: this.param.setBounds,
        ...this.param.orbParam
      });

      this.pixiApp && this.pixiApp.stage.addChild(orb.graphics);

      this.orbs.push(orb);

      this.mittManage.on("destroy", () => {
        orb.destroy();
      });
    }
  }
  start() {
    this.renderFrame(() => {
      if (this.state == "destroy") return false;
      this.orbs.forEach(orb => {
        orb.update();
        orb.render();
      });
      return false;
    });
  }

  resize() {
    if (this.state == "destroy") return false;
    const size = this.param.getSize && this.param.getSize();
    this.pixiApp &&
      this.pixiApp.renderer.resize(
        size ? size.width : window.innerWidth,
        size ? size.height : window.innerHeight
      );
    this.param.onResize && this.param.onResize();
  }

  updatePos(x, y, param) {
    const defaultDur = this.circleState.catch ? 2 : 14;
    const duration = param ? param.duration : defaultDur;
    const pos = this.circleState.catch ? this.circleState.position : { x, y };

    gsap.to(this.originPointer, {
      overwrite: true,
      ...pos,
      duration,
      ease: "Power3.out"
    });
  }

  destroy() {
    if (this.state == "destroy") return false;
    this.state = "destroy";
    this.mittManage.emit("destroy");
  }
}



// WEBPACK FOOTER //
// ./src/utils/orb-effect.js